import React from 'react'
import { isNil } from 'lodash'

import { Pill } from '@/components/pills/Pill'
import { SearchableDropdown } from '@/components/dropdowns/SearchableDropdown'

import { SCOPE_ALL } from './AgenticInsights.constants'
import { formatDateOnly } from './AgenticInsights.runReset'

// The run/scope selector. A run is scoped to one or more playbooks, a set of
// agents, or the whole org; this lists the scopes that have insights (each with
// its insight + calls counts) plus an "All" option that groups every run
// separately (no cross-run totals). When every scope is a playbook (the common
// case) the wording stays "playbook"; otherwise it reads generically.
//
// The list is short and fixed, so the search input stays hidden and the order
// stays as built — "All" first, then the scopes behind a separator.
export const RunScopeSelector = ({ summary, scope, onChange }) => {
  const { scopes, scopeCount, allPlaybook, runDate } = summary

  const scopeNoun = (count) => {
    if (allPlaybook) {
      return count === 1 ? 'playbook' : 'playbooks'
    }
    return count === 1 ? 'scope' : 'scopes'
  }

  const allLabel = allPlaybook ? 'All playbooks' : 'All insights'
  const selected = scope === SCOPE_ALL ? null : scopes.find((entry) => entry.key === scope)
  const triggerMeta = (() => {
    if (!selected) {
      return `${scopeCount} ${scopeNoun(scopeCount)}`
    }
    const insightCount = selected.agentCount + selected.execCount
    const insightsLabel = `${insightCount} ${insightCount === 1 ? 'insight' : 'insights'}`
    if (isNil(selected.callCount)) {
      return insightsLabel
    }
    return `${insightsLabel} · ${selected.callCount.toLocaleString()} calls analyzed`
  })()

  // A single-facet scope (one playbook, today's norm) reads as plain text; a
  // combined scope shows a chip per facet so the combination is legible. The
  // plain name stays the option label so the trigger reads as text either way.
  const facetChips = (entry) => {
    if (entry.facets.length === 1) {
      return undefined
    }
    return (
      <span className="flex-wrap small-gap">
        {entry.facets.map((facet) => (
          <Pill
            key={facet.label}
            xs
            outlined
            brand={facet.type === 'playbook'}
            informative={facet.type !== 'playbook'}
          >
            {facet.label}
          </Pill>
        ))}
      </span>
    )
  }

  const options = [
    {
      value: SCOPE_ALL,
      label: allLabel,
      description: 'every run, grouped separately',
      meta: `${scopeCount} ${scopeNoun(scopeCount)}`,
    },
    ...scopes.map((entry, index) => ({
      value: entry.key,
      label: entry.name,
      labelNode: facetChips(entry),
      description: `${entry.agentCount} agent · ${entry.execCount} executive`,
      meta: isNil(entry.callCount) ? undefined : `${entry.callCount.toLocaleString()} calls`,
      hasSeparator: index === 0,
    })),
  ]

  const resultsHeader = (() => {
    if (!runDate) {
      return undefined
    }
    const runDay = formatDateOnly(runDate, 'MMMM d, yyyy')
    if (allPlaybook) {
      return `We analyzed ${scopeCount} of your ${scopeNoun(scopeCount)} on ${runDay}.`
    }
    return `Analyzed on ${runDay}.`
  })()

  return (
    <SearchableDropdown
      hideSearch
      wide
      selectedOptionFirst={false}
      options={options}
      selected={scope}
      handleSelect={onChange}
      additionalLabel="Analysis for"
      triggerMeta={triggerMeta}
      resultsHeader={resultsHeader}
      dataTestId="run-scope"
    />
  )
}
